import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { EgsGuestSlot, EgsRoomGroup } from './types';

const GUEST_TYPE_TO_SLOT: Record<string, EgsGuestSlot> = {
  'extra adult': 'adult',
  'extra child': 'child',
  'extra infant': 'infant',
  'extra teenager': 'teenager',
};

const ARCHIVED_NAME_PREFIX = /^archived\s+/i;

// One list, so nothing can disagree about slot order.
export const EGS_SLOTS: Array<EgsGuestSlot> = ['adult', 'teenager', 'child', 'infant'];

export const EGS_SLOT_LABEL: Record<EgsGuestSlot, string> = {
  adult: 'Adult',
  child: 'Child',
  infant: 'Infant',
  teenager: 'Teenager',
};

/** Margin on sell, the same way a hand-entered surcharge is measured. */
export function getEgsMarginPercent(cost: number, sell: number) {
  return sell > 0 ? ((sell - cost) / sell) * 100 : null;
}

export function getGuestSlot(extraGuestType: string | null | undefined): EgsGuestSlot | null {
  return GUEST_TYPE_TO_SLOT[(extraGuestType ?? '').trim().toLowerCase()] ?? null;
}

/**
 * There is no room lookup on the Salesforce object: CMs put the room in the name, and supersede a row by
 * prefixing it with ARCHIVED. So the room is the name with that prefix removed.
 */
export function getRoomLabel(name: string | null | undefined) {
  return (name ?? '').replace(ARCHIVED_NAME_PREFIX, '').trim();
}

export function isArchivedByName(name: string | null | undefined) {
  return ARCHIVED_NAME_PREFIX.test((name ?? '').trim());
}

/**
 * Salesforce carries an explicit archive__c flag, but it is not populated on every record, so a null
 * means the sync never wrote the field rather than "live" and the name is the only signal left.
 */
export function isArchivedRow(row: OpportunityRelatedExtraGuestSurcharge) {
  if (row.archive__c == null) {
    return isArchivedByName(row.name);
  }

  return row.archive__c;
}

/**
 * Renamed but never ticked. Neither signal is safe to act on: the name discards a surcharge Salesforce
 * still calls live, the flag writes a price the CM renamed to retire.
 */
export function hasArchiveDisagreement(row: OpportunityRelatedExtraGuestSurcharge) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare -- an unsynced null is not a disagreement, so it cannot collapse to !row.archive__c
  return row.archive__c === false && isArchivedByName(row.name);
}

export function groupEgsByRoom(surcharges: Array<OpportunityRelatedExtraGuestSurcharge>): Array<EgsRoomGroup> {
  const byRoom = new Map<string, Array<OpportunityRelatedExtraGuestSurcharge>>();

  for (const surcharge of surcharges) {
    const roomLabel = getRoomLabel(surcharge.name);
    byRoom.set(roomLabel, [...(byRoom.get(roomLabel) ?? []), surcharge]);
  }

  return [...byRoom.entries()]
    .map(([roomLabel, rows]) => ({
      conflictingSlots: getConflictingSlots(rows),
      roomLabel,
      rows,
      slots: getRoomSlots(rows),
    }))
    .sort((a, b) => a.roomLabel.localeCompare(b.roomLabel));
}

// Superseded rows stay in the detail table, but a slot whose only row is archived is not live.
function getRoomSlots(rows: Array<OpportunityRelatedExtraGuestSurcharge>): Array<EgsGuestSlot> {
  const slots = rows
    .filter((row) => !isArchivedRow(row))
    .map((row) => getGuestSlot(row.extra_guest_type__c))
    .filter((slot): slot is EgsGuestSlot => !!slot);

  return sortBySlotOrder([...new Set(slots)]);
}

function sortBySlotOrder(slots: Array<EgsGuestSlot>): Array<EgsGuestSlot> {
  return [...slots].sort((a, b) => EGS_SLOTS.indexOf(a) - EGS_SLOTS.indexOf(b));
}

export function formatSlotList(slots: Array<EgsGuestSlot>) {
  if (slots.length <= 1) {
    return slots[0] ?? '';
  }

  return `${slots.slice(0, -1).join(', ')} and ${slots[slots.length - 1]}`;
}

/**
 * Only live rows compete: an archived row beside its live replacement is how a CM supersedes a surcharge.
 * Per-record, so it can differ from the apply, which resolves across every record aimed at one room rate.
 */
function getConflictingSlots(rows: Array<OpportunityRelatedExtraGuestSurcharge>) {
  const counts = new Map<EgsGuestSlot, number>();

  for (const row of rows) {
    if (isArchivedRow(row)) {
      continue;
    }

    const slot = getGuestSlot(row.extra_guest_type__c);
    if (slot) {
      counts.set(slot, (counts.get(slot) ?? 0) + 1);
    }
  }

  return sortBySlotOrder([...counts.entries()].filter(([, count]) => count > 1).map(([slot]) => slot));
}

export function countRoomRatesWithEgs(extraGuestSurchargesByRoomRate: Record<string, Array<unknown>>) {
  return Object.values(extraGuestSurchargesByRoomRate).filter((records) => records.length > 0).length;
}
